import { useState, useEffect } from 'react';
import { artists as mockArtists } from '../mocks/artists';
import { artistDetails as mockArtistDetails } from '../mocks/artistDetails';

export interface Artist {
  id: string;
  name: string;
  slug: string;
  type: 'illustrator' | 'designer' | 'writer' | 'painter';
  specialty: string;
  image: string;
  tagline: string;
}

export interface ArtistDetail extends Artist {
  bio: string[];
  heroImage: string;
  clients: string[];
  awards: { year: string; title: string }[];
  testimonial?: {
    quote: string;
    author: string;
    company: string;
  };
  social: {
    instagram?: string;
    twitter?: string;
    website?: string;
  };
  portfolio: {
    id: string;
    title: string;
    category: string;
    year: string;
    image: string;
  }[];
}

const STORAGE_KEY = 'pavane_artists';
const DETAILS_STORAGE_KEY = 'pavane_artist_details';

export function useArtists() {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [artistDetails, setArtistDetails] = useState<Record<string, ArtistDetail>>({});

  // 초기 데이터 로드
  useEffect(() => {
    const storedArtists = localStorage.getItem(STORAGE_KEY);
    const storedDetails = localStorage.getItem(DETAILS_STORAGE_KEY);

    if (storedArtists) {
      setArtists(JSON.parse(storedArtists));
    } else {
      // 초기 seed 데이터
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mockArtists));
      setArtists(mockArtists);
    }

    if (storedDetails) {
      setArtistDetails(JSON.parse(storedDetails));
    } else {
      // 초기 seed 데이터
      localStorage.setItem(DETAILS_STORAGE_KEY, JSON.stringify(mockArtistDetails));
      setArtistDetails(mockArtistDetails);
    }
  }, []);

  // 아티스트 목록 저장
  const saveArtists = (newArtists: Artist[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newArtists));
    setArtists(newArtists);
  };

  // 아티스트 상세 정보 저장
  const saveArtistDetails = (newDetails: Record<string, ArtistDetail>) => {
    localStorage.setItem(DETAILS_STORAGE_KEY, JSON.stringify(newDetails));
    setArtistDetails(newDetails);
  };

  // 아티스트 추가
  const addArtist = (artist: ArtistDetail) => {
    const newArtist: Artist = {
      id: artist.id,
      name: artist.name,
      slug: artist.slug,
      type: artist.type,
      specialty: artist.specialty,
      image: artist.image,
      tagline: artist.tagline,
    };

    const newArtists = [...artists, newArtist];
    const newDetails = { ...artistDetails, [artist.slug]: artist };

    saveArtists(newArtists);
    saveArtistDetails(newDetails);
  };

  // 아티스트 수정
  const updateArtist = (slug: string, artist: ArtistDetail) => {
    const newArtists = artists.map(a => 
      a.slug === slug 
        ? {
            id: artist.id,
            name: artist.name,
            slug: artist.slug,
            type: artist.type,
            specialty: artist.specialty,
            image: artist.image,
            tagline: artist.tagline,
          }
        : a
    );

    const newDetails = { ...artistDetails, [artist.slug]: artist };

    saveArtists(newArtists);
    saveArtistDetails(newDetails);
  };

  // 아티스트 삭제
  const deleteArtist = (slug: string) => {
    const newArtists = artists.filter(a => a.slug !== slug);
    const newDetails = { ...artistDetails };
    delete newDetails[slug];

    saveArtists(newArtists);
    saveArtistDetails(newDetails);
  };

  // 타입별 아티스트 가져오기
  const getArtistsByType = (type: 'illustrator' | 'designer' | 'writer' | 'painter') => {
    return artists.filter(a => a.type === type);
  };

  // 아티스트 상세 정보 가져오기
  const getArtistDetail = (slug: string) => {
    return artistDetails[slug];
  };

  // slug로 아티스트 가져오기
  const getArtistBySlug = (slug: string): ArtistDetail | undefined => {
    return artistDetails[slug];
  };

  // 관련 아티스트 가져오기
  const getRelatedArtists = (currentSlug: string, type: 'illustrator' | 'designer' | 'writer' | 'painter'): ArtistDetail[] => {
    return Object.values(artistDetails)
      .filter((artist) => artist.type === type && artist.slug !== currentSlug)
      .slice(0, 3);
  };

  return {
    artists,
    artistDetails,
    addArtist,
    updateArtist,
    deleteArtist,
    getArtistsByType,
    getArtistDetail,
    getArtistBySlug,
    getRelatedArtists,
  };
}
